// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Search, Dropdown, Button } from '@carbon/react';
import { ArrowLeft } from '@carbon/icons-react';
import { useDigitalTwin, TELEMETRY_POLL_INTERVAL_MS } from '../../hooks/useDigitalTwin';
import {
  getEntityDisplayType,
  getEntityCode,
  groupMeasurements,
  isAssetEntityType,
  formatAttributeValue,
  aggregateTelemetryState,
  buildTelemetryRecencyMap,
  isTelemetryLive,
} from '../../utils/entityHelpers';
import type { GroupedMeasurements } from '../../utils/entityHelpers';
import { buildHierarchy, findNodeById, getAncestorIds } from '../../utils/entityHierarchy';
import type { HierarchyNode } from '../../utils/entityHierarchy';
import type { Entity } from '../../types/digitalTwin';
import { EntitySchemas } from '../../config/entity-schemas';
import { Loading } from '../../components/shell/ScreenHelpers';
import './DCAssets.css';

// ---------------------------------------------------------------------------
// Small presentational helpers
// ---------------------------------------------------------------------------

/**
 * Live / Unknown indicator driven by telemetry recency. An asset is "Live"
 * (green pulsing dot) when it has telemetry within TELEMETRY_LIVE_WINDOW_MS,
 * otherwise "Unknown" (neutral dot).
 */
function LiveIndicator({ live }: { live: boolean }) {
  return (
    <span className={`dca-pill ${live ? 'dca-live' : 'dca-unknown'}`}>
      <span className={`dca-pill-dot ${live ? 'dca-live-dot' : ''}`} />
      {live ? 'Live' : 'Unknown'}
    </span>
  );
}

/** Format an ISO timestamp for display, guarding against bad/empty values. */
function formatStamp(value: unknown): string | null {
  if (!value || typeof value !== 'string') return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleString();
}

// ---------------------------------------------------------------------------
// Tree node component (Assets list view)
// ---------------------------------------------------------------------------

interface TreeNodeProps {
  node: HierarchyNode;
  depth: number;
  selectedId: string | null;
  expandedNodes: Set<string>;
  liveIds: Set<string>;
  onSelect: (_entityId: string) => void;
  onToggle: (_entityId: string) => void;
  searchQuery: string;
  typeFilter: string;
}

/** Check if a node or any descendant matches the search/filter criteria. */
function nodeMatchesFilter(node: HierarchyNode, searchQuery: string, typeFilter: string): boolean {
  const label = node.entity.attributes?.label ?? node.entity.id;
  const matchesSearch =
    !searchQuery ||
    label.toLowerCase().includes(searchQuery.toLowerCase()) ||
    node.displayType.toLowerCase().includes(searchQuery.toLowerCase());
  const matchesType = typeFilter === 'all' || node.displayType === typeFilter;

  if (matchesSearch && matchesType) return true;
  return node.children.some(child => nodeMatchesFilter(child, searchQuery, typeFilter));
}

function TreeNodeComponent({
  node,
  depth,
  selectedId,
  expandedNodes,
  liveIds,
  onSelect,
  onToggle,
  searchQuery,
  typeFilter,
}: TreeNodeProps) {
  if (!nodeMatchesFilter(node, searchQuery, typeFilter)) return null;

  const hasChildren = node.children.length > 0;
  const isExpanded = expandedNodes.has(node.entity.id);
  const isSelected = selectedId === node.entity.id;
  const live = liveIds.has(node.entity.id);
  const label = node.entity.attributes?.label ?? node.entity.id;

  return (
    <div className="dca-tree-node">
      <div
        className={`dca-row ${live ? 'dca-st-ok' : 'dca-st-idle'} ${isSelected ? 'dca-row-active' : ''}`}
        role="treeitem"
        tabIndex={0}
        aria-selected={isSelected}
        aria-expanded={hasChildren ? isExpanded : undefined}
        onClick={() => onSelect(node.entity.id)}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelect(node.entity.id);
          }
        }}
      >
        <span className="dca-indent" style={{ width: `${depth * 16}px` }} />
        {hasChildren ? (
          <button
            type="button"
            className={`dca-arrow ${isExpanded ? 'dca-arrow-open' : ''}`}
            onClick={e => {
              e.stopPropagation();
              onToggle(node.entity.id);
            }}
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
          >
            &#9656;
          </button>
        ) : (
          <span className="dca-arrow dca-arrow-spacer" />
        )}
        <span className="dca-code">{getEntityCode(node.entity.type)}</span>
        <span className="dca-row-info">
          <span className="dca-row-name">{label}</span>
          <span className="dca-row-type">{node.displayType}</span>
        </span>
        <LiveIndicator live={live} />
      </div>
      {hasChildren && isExpanded && (
        <div className="dca-tree-children" role="group">
          {node.children.map(child => (
            <TreeNodeComponent
              key={child.entity.id}
              node={child}
              depth={depth + 1}
              selectedId={selectedId}
              expandedNodes={expandedNodes}
              liveIds={liveIds}
              onSelect={onSelect}
              onToggle={onToggle}
              searchQuery={searchQuery}
              typeFilter={typeFilter}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dependency item component (Feeds / Fed by rows in detail view)
// ---------------------------------------------------------------------------

interface DependencyItemProps {
  entity: Entity;
  live: boolean;
  onClick: () => void;
}

function DependencyItem({ entity, live, onClick }: DependencyItemProps) {
  const label = entity.attributes?.label ?? entity.id;

  return (
    <div
      className="dca-row dca-feed-row"
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <span className="dca-code">{getEntityCode(entity.type)}</span>
      <span className="dca-row-info">
        <span className="dca-row-name">{label}</span>
        <span className="dca-row-type">{getEntityDisplayType(entity.type)}</span>
      </span>
      <LiveIndicator live={live} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

function DCAssets() {
  const { site_id } = useParams();
  const { entities, relationships, loading, error, refetch } = useDigitalTwin(site_id, {
    pollIntervalMs: TELEMETRY_POLL_INTERVAL_MS,
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selectedEntityId, setSelectedEntityId] = useState<string | null>(null);
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  // Ticking clock so the "live" window is re-evaluated even when the underlying
  // data hasn't changed (data itself refreshes on the 60s telemetry poll).
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  // Build hierarchy tree
  // Filter to only asset entities (exclude telemetry points, documents, etc.)
  const assetEntities = useMemo(() => entities.filter(e => isAssetEntityType(e.type)), [entities]);
  const roots = useMemo(
    () => buildHierarchy(assetEntities, relationships),
    [assetEntities, relationships]
  );

  // Telemetry recency → liveness. Derived from data already loaded by
  // useDigitalTwin (own state.updatedAt + child telemetry points); recomputed
  // against `now` so it stays fresh across the 60s poll.
  const recency = useMemo(
    () => buildTelemetryRecencyMap(entities, relationships),
    [entities, relationships]
  );
  const liveIds = useMemo(() => {
    const ids = new Set<string>();
    recency.forEach((t, id) => {
      if (isTelemetryLive(t, now)) ids.add(id);
    });
    return ids;
  }, [recency, now]);

  // Initialize expanded nodes when roots first populate (expand first 2 levels).
  // expandedNodes.size is intentionally excluded from deps — we only
  // initialise once when roots arrive, not on every expand/collapse.
  useEffect(() => {
    if (roots.length > 0 && expandedNodes.size === 0) {
      const initial = new Set<string>();
      for (const root of roots) {
        initial.add(root.entity.id);
        for (const child of root.children) {
          initial.add(child.entity.id);
        }
      }
      setExpandedNodes(initial);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roots]);

  // Available asset types for filter dropdown
  const assetTypes = useMemo(() => {
    const types = new Set<string>();
    function collectTypes(nodes: HierarchyNode[]) {
      for (const n of nodes) {
        types.add(n.displayType);
        collectTypes(n.children);
      }
    }
    collectTypes(roots);
    return ['all', ...Array.from(types).sort()];
  }, [roots]);

  // Count visible assets
  const totalAssets = useMemo(() => {
    let count = 0;
    function countNodes(nodes: HierarchyNode[]) {
      for (const n of nodes) {
        if (nodeMatchesFilter(n, searchQuery, typeFilter)) count++;
        countNodes(n.children);
      }
    }
    countNodes(roots);
    return count;
  }, [roots, searchQuery, typeFilter]);

  // Selected entity data
  const selectedEntity = useMemo(
    () => (selectedEntityId ? entities.find(e => e.id === selectedEntityId) : null),
    [selectedEntityId, entities]
  );

  // Aggregate telemetry from child entities (decomposed model) + parent's own state
  const selectedAggregatedState = useMemo(
    () =>
      selectedEntity ? aggregateTelemetryState(selectedEntity, entities, relationships) : undefined,
    [selectedEntity, entities, relationships]
  );

  const selectedGroupedMeasurements = useMemo<GroupedMeasurements[]>(
    () => groupMeasurements(selectedAggregatedState),
    [selectedAggregatedState]
  );

  // Feeds (downstream): entities this asset feeds power to
  const feedsEntities = useMemo(() => {
    if (!selectedEntityId) return [];
    const targetIds = new Set(
      relationships
        .filter(r => r.source === selectedEntityId && r.type === 'feeds')
        .map(r => r.target)
    );
    return entities.filter(e => targetIds.has(e.id));
  }, [selectedEntityId, relationships, entities]);

  // Fed By (upstream): entities that feed power to this asset
  const fedByEntities = useMemo(() => {
    if (!selectedEntityId) return [];
    const sourceIds = new Set(
      relationships
        .filter(r => r.target === selectedEntityId && (r.type === 'feeds' || r.type === 'powers'))
        .map(r => r.source)
    );
    return entities.filter(e => sourceIds.has(e.id));
  }, [selectedEntityId, relationships, entities]);

  // Handlers
  const handleToggle = useCallback((entityId: string) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(entityId)) {
        next.delete(entityId);
      } else {
        next.add(entityId);
      }
      return next;
    });
  }, []);

  const navigateToEntity = useCallback(
    (entityId: string) => {
      setSelectedEntityId(entityId);
      // Auto-expand the path to this node in the tree
      const node = findNodeById(roots, entityId);
      if (node) {
        const ancestorIds = getAncestorIds(node);
        setExpandedNodes(prev => {
          const next = new Set(prev);
          for (const id of ancestorIds) next.add(id);
          return next;
        });
      }
    },
    [roots]
  );

  const toggleTelemetryGroup = useCallback((group: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(group)) {
        next.delete(group);
      } else {
        next.add(group);
      }
      return next;
    });
  }, []);

  if (loading) {
    return (
      <div className="dca-state">
        <Loading label="Loading assets…" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="dca-state">
        <p className="dca-state-title">Failed to load asset data</p>
        <p className="dca-state-detail">{error}</p>
        <Button kind="tertiary" size="sm" onClick={refetch}>
          Retry
        </Button>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Detail view
  // -------------------------------------------------------------------------
  if (selectedEntity) {
    const displayType = getEntityDisplayType(selectedEntity.type);
    const stamp = formatStamp(selectedAggregatedState?.updatedAt);
    const tierRaw = selectedEntity.attributes?.tier ?? selectedEntity.attributes?.criticality;
    const tier = tierRaw != null ? formatAttributeValue(tierRaw) : '';
    const hasSOP = Boolean(selectedEntity.attributes?.hasSOP);
    const hasDoc = Boolean(selectedEntity.attributes?.hasDocument);
    const schemaFields = (EntitySchemas[selectedEntity.type]?.attributeFields ?? []).filter(
      field =>
        selectedEntity.attributes?.[field] != null &&
        field !== 'label' &&
        field !== 'hasSOP' &&
        field !== 'hasDocument'
    );
    const isMonoField = (f: string) => /id|serial/i.test(f);

    return (
      <div className="dca-detail-page">
        <button type="button" className="dca-back" onClick={() => setSelectedEntityId(null)}>
          <ArrowLeft size={16} />
          All assets
        </button>

        <div className="dca-detail-head">
          <div className="dca-detail-head-main">
            <h1 className="dca-detail-title">
              {selectedEntity.attributes?.label ?? selectedEntity.id}
            </h1>
            <div className="dca-detail-type">{displayType}</div>
            {tier && <div className="dca-detail-tier">{tier}</div>}
          </div>
          <div className="dca-detail-head-side">
            <LiveIndicator live={liveIds.has(selectedEntity.id)} />
            {stamp && <span className="dca-stamp">{stamp}</span>}
          </div>
        </div>

        {/* Real-time telemetry */}
        <div className="dca-section-label">Real-time telemetry</div>
        {selectedGroupedMeasurements.length > 0 ? (
          <div className="dca-tel-groups">
            {selectedGroupedMeasurements.map(group => {
              const expanded = !collapsedGroups.has(group.group);
              return (
                <div key={group.group} className="dca-tel-group">
                  <button
                    type="button"
                    className="dca-tel-header"
                    onClick={() => toggleTelemetryGroup(group.group)}
                  >
                    <span className={`dca-arrow ${expanded ? 'dca-arrow-open' : ''}`}>&#9656;</span>
                    <span className="dca-tel-name">{group.group}</span>
                    <span className="dca-tel-count">{group.entries.length}</span>
                  </button>
                  {expanded && (
                    <div className="dca-tel-body">
                      {group.entries.map(m => (
                        <div key={m.rawKey} className="dca-tel-row">
                          <span className="dca-tel-k">{m.key}</span>
                          <span className="dca-tel-v">{m.display}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="dca-empty-box">No measurements available</div>
        )}
        {stamp && <div className="dca-tel-updated">Last updated: {stamp}</div>}

        {/* Standard operating procedures */}
        <div className="dca-section-label">Standard operating procedures</div>
        {hasSOP || hasDoc ? (
          <div className="dca-list-card">
            {hasSOP && (
              <div className="dca-sop-row">
                <span className="dca-sop-id">SOP</span>
                <span className="dca-sop-label">SOP available</span>
              </div>
            )}
            {hasDoc && (
              <div className="dca-sop-row">
                <span className="dca-sop-id">DOC</span>
                <span className="dca-sop-label">Documentation available</span>
              </div>
            )}
          </div>
        ) : (
          <div className="dca-empty-box">No SOPs linked to this asset.</div>
        )}

        {/* Feeds (downstream) */}
        <div className="dca-section-label">Feeds ({feedsEntities.length})</div>
        {feedsEntities.length > 0 ? (
          <div className="dca-list-card">
            {feedsEntities.map(entity => (
              <DependencyItem
                key={entity.id}
                entity={entity}
                live={liveIds.has(entity.id)}
                onClick={() => navigateToEntity(entity.id)}
              />
            ))}
          </div>
        ) : (
          <div className="dca-empty-box">Nothing downstream of this asset.</div>
        )}

        {/* Fed by (upstream) */}
        <div className="dca-section-label">Fed by ({fedByEntities.length})</div>
        {fedByEntities.length > 0 ? (
          <div className="dca-list-card">
            {fedByEntities.map(entity => (
              <DependencyItem
                key={entity.id}
                entity={entity}
                live={liveIds.has(entity.id)}
                onClick={() => navigateToEntity(entity.id)}
              />
            ))}
          </div>
        ) : (
          <div className="dca-empty-box">No upstream source recorded.</div>
        )}

        {/* Entity info */}
        <div className="dca-section-label">Entity info</div>
        <div className="dca-list-card">
          <div className="dca-entity-grid">
            <span className="dca-entity-k">Type</span>
            <span className="dca-entity-v">{displayType}</span>
            <span className="dca-entity-k">Entity ID</span>
            <span className="dca-entity-v dca-mono">{selectedEntity.id}</span>
            {schemaFields.map(field => (
              <React.Fragment key={field}>
                <span className="dca-entity-k">{field}</span>
                <span className={`dca-entity-v ${isMonoField(field) ? 'dca-mono' : ''}`}>
                  {formatAttributeValue(selectedEntity.attributes[field])}
                </span>
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // List view
  // -------------------------------------------------------------------------
  return (
    <div className="dca-list-page">
      <div className="dca-list-head">
        <div className="dca-list-title-wrap">
          <h1 className="dca-h1">Assets</h1>
          <span className="dca-count">{totalAssets}</span>
        </div>
        <Dropdown
          id="asset-type-filter"
          className="dca-type-filter"
          type="inline"
          size="md"
          titleText="Type"
          label="All Types"
          items={assetTypes}
          selectedItem={typeFilter}
          itemToString={(t: string | null) => (t === 'all' ? 'All Types' : (t ?? ''))}
          onChange={({ selectedItem }: { selectedItem: string | null }) =>
            setTypeFilter(selectedItem ?? 'all')
          }
        />
      </div>

      <div className="dca-search">
        <Search
          id="asset-search"
          size="md"
          labelText="Search assets"
          placeholder="Search assets by name, type, or ID…"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          onClear={() => setSearchQuery('')}
        />
      </div>

      <div className="dca-list" role="tree" aria-label="Asset hierarchy">
        {roots.map(root => (
          <TreeNodeComponent
            key={root.entity.id}
            node={root}
            depth={0}
            selectedId={selectedEntityId}
            expandedNodes={expandedNodes}
            liveIds={liveIds}
            onSelect={navigateToEntity}
            onToggle={handleToggle}
            searchQuery={searchQuery}
            typeFilter={typeFilter}
          />
        ))}
        {totalAssets === 0 && (
          <div className="dca-empty-row">No assets match your search or filter.</div>
        )}
      </div>
    </div>
  );
}

export default DCAssets;
